import { EssayQuestionResponse } from './../../../models/essay-pilot/essayQuestionResponse';
import { EssayPilotService } from './../../../services/essay-pilot/essay-pilot.service';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { InstituteCommonDetailsResponse } from '../../../models/institute/instituteCommonDetailsResponse';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';
import { InstituteService } from '../../../services/institute/institute.service';

@Component({
  selector: 'app-add-institute-questions',
  standalone: true,
  imports: [ButtonModule, ReactiveFormsModule, CommonModule, InputTextModule, DropdownModule, AutoCompleteModule],
  templateUrl: './add-institute-questions.component.html',
  styleUrl: './add-institute-questions.component.scss'
})
export class AddInstituteQuestionsComponent implements OnInit, OnChanges, AfterViewInit {

  constructor(
    private formBuilder: FormBuilder,
    private essayPilotService: EssayPilotService,
    private messageService: MessageService,
    private instituteService: InstituteService,
  ) {}

  @Input() institute: InstituteCommonDetailsResponse | null | undefined;
  @Input() essayQuestion: EssayQuestionResponse | null | undefined;
  @Input() visible!: boolean;
  @Output() closeDialog = new EventEmitter();

  majors: string[] = [];
  questionsForm!: FormGroup;
  submitted = false;
  isLoading = false;
  private validatorsSetup = false;

  degreePrefs = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsAndMastersDegree], key: CourseLevelEnum.BachelorsAndMastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DualDegree], key: CourseLevelEnum.DualDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.FoundationDegree], key: CourseLevelEnum.FoundationDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PostgraduateCertificate], key: CourseLevelEnum.PostgraduateCertificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PostgraduateDiploma], key: CourseLevelEnum.PostgraduateDiploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.PreSessionalProgram], key: CourseLevelEnum.PreSessionalProgram },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.UndergraduateCertificate], key: CourseLevelEnum.UndergraduateCertificate },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.UndergraduateDiploma], key: CourseLevelEnum.UndergraduateDiploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Vocational], key: CourseLevelEnum.Vocational }
  ];

  @ViewChild('courseNameAutoComplete')
    courseNameAutoComplete!: AutoComplete;
  ngAfterViewInit() {
    if (this.courseNameAutoComplete) {
      const inputElement = this.courseNameAutoComplete.el.nativeElement.querySelector('input');
      if (inputElement) {
        inputElement.addEventListener('keydown', (event: { key: string; preventDefault: () => void; }) => {
          if (event.key === 'Enter') {
            // Prevent the default form submission behavior
            event.preventDefault();

            // Programmatically unfocus the input, which will trigger (onBlur)
            inputElement.blur();
          }
        });
      }
    }
  }

  ngOnInit(): void {
    // Initialize form empty
    this.initializeForm();

    // Setup conditional validators only once
    for (let i = 1; i <= 5; i++) {
      const questionControl = this.questionsForm.get(`question${i}`);
      const wordLimitControl = this.questionsForm.get(`question${i}WordLimit`);

      questionControl?.valueChanges.subscribe(value => {
        if (value && value.trim() !== '') {
          wordLimitControl?.setValidators([Validators.required, Validators.min(1)]);
        } else {
          wordLimitControl?.clearValidators();
        }
        wordLimitControl?.updateValueAndValidity({ emitEvent: false });
      });
    }
    this.validatorsSetup = true;
  }

  patchForm(){
    this.questionsForm.patchValue({
      courseLevel: this.essayQuestion?.courseLevel ?? null,
      courseName: this.essayQuestion?.courseName ?? null,
      question1: this.essayQuestion?.question1 ?? '',
      question2: this.essayQuestion?.question2 ?? '',
      question3: this.essayQuestion?.question3 ?? '',
      question4: this.essayQuestion?.question4 ?? '',
      question5: this.essayQuestion?.question5 ?? '',
      question1WordLimit: this.essayQuestion?.question1WordLimit ?? null,
      question2WordLimit: this.essayQuestion?.question2WordLimit ?? null,
      question3WordLimit: this.essayQuestion?.question3WordLimit ?? null,
      question4WordLimit: this.essayQuestion?.question4WordLimit ?? null,
      question5WordLimit: this.essayQuestion?.question5WordLimit ?? null
    }, { emitEvent: false });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['essayQuestion'] && this.questionsForm) {
      this.questionsForm.reset();

      if (this.essayQuestion) {
        this.patchForm();
      }
    }

    // Keep the visible-based reset for dialog close
    if (changes['visible'] &&
        changes['visible'].previousValue === true &&
        changes['visible'].currentValue === false) {
      this.resetQuestionsForm();
    }
  }


  initializeForm() {
    this.questionsForm = this.formBuilder.group({
      courseLevel: [null, Validators.required],
      courseName: [null, Validators.required],
      question1: [''],
      question2: [''],
      question3: [''],
      question4: [''],
      question5: [''],
      question1WordLimit: [null],
      question2WordLimit: [null],
      question3WordLimit: [null],
      question4WordLimit: [null],
      question5WordLimit: [null]
    });
  }

  getControl(name: string) {
    return this.questionsForm.get(name);
  }

  onSubmit() {
    this.submitted = true;
    if (this.questionsForm.invalid) {
      return;
    }
    this.isLoading = true;

    const courseName = this.questionsForm.get('courseName')?.value;
    const isMajorAlredyInDB = this.majors.includes(courseName);
    if(!isMajorAlredyInDB){
      this.instituteService.addInstituteCourseNames({
      instituteId: this.institute?.id,
      courseLevel: this.questionsForm.get('courseLevel')?.value,
      courseName: courseName
      }).subscribe({
          next: () => {
            this.updateChecklistAfterAdd();
          },
          error: (error) => {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to add new course name: ${error.message}` });
            console.warn(`Error while adding new course name:${error}`);
          }
        });
    }else{
      this.updateChecklistAfterAdd();
    }
  }

  private updateChecklistAfterAdd(){
    this.essayPilotService.addInstituteQuestions(this.institute?.id ?? '',
    this.questionsForm.value).subscribe({
      next: () => {
        this.isLoading = false;
        this.submitted = false;
        this.messageService.add({ severity: "success", summary: "Success", detail: "Questions are updated successfully!" });
        this.closeDialog.emit();
        this.questionsForm.reset();
      },
      error: (err) => {
        console.warn(`error while updating questions: ${err}`);
        this.isLoading = false;
      }
    });
  }

  onCourseChange($event: DropdownChangeEvent) {
    // this.essayPilotService.getInstituteQuestions({
    //   instituteId: this.institute?.id ?? '',
    //   courseLevel: $event.value,
    //   courseName: this.questionsForm.get('courseName')?.value
    // }).subscribe({
    //   next: (result) => {
    //     if (result) {
    //       this.essayQuestion = result;
    //       this.essayQuestion.courseLevel = $event.value;
    //     } else {
    //       this.essayQuestion = { courseLevel: $event.value, courseName: '' };
    //     }
    //     this.patchForm();
    //   },
    //   error: (error) => {
    //     console.warn(`error while retriving checklist:${error}`);
    //   }
    // });
  }

  filterIntendedMajors(event: AutoCompleteCompleteEvent){
    if(event.query.length > 3){
      this.instituteService.listInstituteCourseNames({
        courseLevel: this.questionsForm.value.courseLevel,
        courseName: event.query
      }).subscribe({
        next: (response) => {
          this.majors = response.result;
        }
      })
    }else{
      this.majors = [event.query]
    }
  }

  onCourseNameChange() {
    // Only proceed if a course level is selected, as it's a required parameter.
    if (this.questionsForm.get('courseLevel')?.value === null
      || this.questionsForm.get('courseName')?.value === null) {
        return;
    }

    this.essayPilotService.getInstituteQuestions({
        instituteId: this.institute?.id ?? '',
        courseLevel: this.questionsForm.get('courseLevel')?.value,
        courseName: this.questionsForm.get('courseName')?.value
    }).subscribe({
        next: (result) => {
            if (result) {
                this.essayQuestion = result;
                this.essayQuestion.courseLevel = this.questionsForm.get('courseLevel')?.value;
            } else {
                this.essayQuestion = { courseLevel: this.questionsForm.get('courseLevel')?.value, courseName: this.questionsForm.get('courseName')?.value };
            }
            this.patchForm();
        },
        error: (error) => {
            console.warn(`error while retrieving checklist: ${error}`);
        }
    });
  }

  resetQuestionsForm(){
    this.questionsForm.reset();
  }
}
